import React, { useState } from "react";
import { X, Phone, EnvelopeSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogOverlay, DialogPortal } from "../components/ui/dialog";
import ArtDecoDivider from "../components/ArtDecoDivider";

const PHONE_DISPLAY = "(203) 631-7573";
const PHONE_TEL = "+12036317573";
const EMAIL = "TheSpiritedGent@gmail.com";

// Two hands exchanging a joint at a table — clean, social, no nightlife/innuendo.
const BG_IMAGE =
    "https://images.unsplash.com/photo-1777148129891-736d7afc4563?fm=jpg&q=75&w=2400&auto=format&fit=crop";

const AMENITIES = [
    { emoji: "🥃", label: "Craft Cocktails" },
    { emoji: "🎯", label: "Custom Stations" },
    { emoji: "✨", label: "Ambient Enhancements" },
    { emoji: "🌿", label: "Curated Touches" },
    { emoji: "🎪", label: "Full Atmosphere" },
];

export default function Beyond() {
    const [open, setOpen] = useState(false);

    return (
        <div className="relative bg-obsidian min-h-screen" data-testid="page-beyond">
            {/* Background image — sits behind all content for the whole page */}
            <div className="absolute inset-0 z-0" aria-hidden="true">
                <img
                    src={BG_IMAGE}
                    alt=""
                    className="w-full h-full object-cover opacity-70"
                />
                {/* Dark gradient overlay — keeps the image atmospheric and content legible */}
                <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/55 to-obsidian" />
                <div className="absolute inset-0 bg-gradient-to-r from-obsidian/30 via-transparent to-obsidian/40" />
            </div>

            {/* Decorative gold radial glow — top right */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-32 -right-32 w-[620px] h-[620px] rounded-full z-0"
                style={{
                    background:
                        "radial-gradient(circle at center, rgba(197,160,89,0.22) 0%, rgba(197,160,89,0.08) 35%, rgba(197,160,89,0) 70%)",
                    filter: "blur(6px)",
                }}
            />

            <div className="relative z-10 max-w-[800px] mx-auto px-6 lg:px-12 pt-20 pb-32 text-center">
                {/* Outlined pill */}
                <div
                    className="inline-block px-4 py-1.5 border border-gold/50 text-gold text-xs tracking-overline uppercase mb-10"
                    data-testid="beyond-pill"
                >
                    For Select Clientele
                </div>

                {/* Italic serif heading */}
                <h1
                    className="font-display italic text-white text-6xl md:text-7xl mb-4"
                    data-testid="beyond-heading"
                >
                    Beyond the Bar
                </h1>

                <ArtDecoDivider />

                {/* Two paragraphs */}
                <p className="text-white/80 text-base md:text-lg font-light leading-relaxed mb-6 mt-2">
                    The Spirited Gentleman specializes in crafting unforgettable private
                    events. For the right crowd, we offer a fully elevated atmosphere — from
                    handcrafted cocktail service to curated ambient stations and signature
                    touches that take your gathering from a great party to a legendary one.
                </p>
                <p className="text-white/70 text-base md:text-lg font-light leading-relaxed mb-16">
                    Our premium packages are tailored privately to each client. If you're
                    looking for something beyond the standard bar setup, you're in the right
                    place.
                </p>

                {/* Amenity tiles */}
                <div
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-14"
                    data-testid="beyond-amenities"
                >
                    {AMENITIES.map((a) => (
                        <div
                            key={a.label}
                            data-testid={`amenity-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="bg-obsidian-soft/80 backdrop-blur-sm border border-white/10 hover:border-gold/40 transition-colors duration-300 px-3 py-5 flex flex-col items-center justify-center gap-2"
                        >
                            <span className="text-2xl leading-none" aria-hidden="true">
                                {a.emoji}
                            </span>
                            <span className="text-white/80 text-xs md:text-sm font-sans tracking-wide">
                                {a.label}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Italic muted gold line */}
                <p
                    className="font-display italic text-gold/70 text-base md:text-lg mb-8"
                    data-testid="beyond-tagline"
                >
                    Reach out directly to discuss what we can put together for you.
                </p>

                {/* Gold CTA */}
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="btn-gold"
                    data-testid="beyond-cta-talk-privately"
                >
                    Let's Talk Privately
                </button>
            </div>

            {/* Modal */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogPortal>
                    <DialogOverlay className="bg-black/80 backdrop-blur-sm" />
                    <DialogContent
                        className="sm:max-w-md bg-[#0A0A0A] border border-gold/30 text-white p-0 [&>button]:hidden"
                        data-testid="beyond-modal"
                    >
                        <div className="relative p-8 md:p-10 text-center">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label="Close"
                                className="absolute top-4 right-4 text-white/60 hover:text-gold transition-colors p-1"
                                data-testid="beyond-modal-close"
                            >
                                <X size={20} weight="thin" />
                            </button>

                            <div className="text-gold text-xs tracking-overline uppercase mb-2">
                                Private Inquiry
                            </div>
                            <h3 className="font-display italic text-white text-4xl mb-6">
                                Let's Talk
                            </h3>

                            <p className="text-white/70 text-sm md:text-base font-light leading-relaxed mb-8">
                                The full Spirited Gentleman experience is discussed privately
                                with each client. Reach out directly and we'll put together
                                something tailored specifically to you and your crowd.
                            </p>

                            <a
                                href={`tel:${PHONE_TEL}`}
                                className="inline-flex items-center justify-center gap-3 font-display text-gold text-3xl md:text-4xl tracking-wide border-b border-gold/30 hover:border-gold pb-2 transition-colors"
                                data-testid="beyond-modal-phone"
                            >
                                <Phone size={26} weight="thin" />
                                {PHONE_DISPLAY}
                            </a>
                            <div className="text-white/40 text-xs italic font-sans mt-3 mb-8">
                                Text or call — discretion guaranteed
                            </div>

                            <div className="flex items-center justify-center gap-3 text-white/40 text-xs tracking-overline uppercase mb-4">
                                <span className="w-8 h-px bg-white/20" />
                                Or
                                <span className="w-8 h-px bg-white/20" />
                            </div>

                            <a
                                href={`mailto:${EMAIL}`}
                                className="inline-flex items-center gap-2 text-gold/80 hover:text-gold text-sm tracking-wide transition-colors"
                                data-testid="beyond-modal-email"
                            >
                                <EnvelopeSimple size={16} weight="thin" />
                                {EMAIL}
                            </a>
                        </div>
                    </DialogContent>
                </DialogPortal>
            </Dialog>
        </div>
    );
}
